import axios from 'axios';
import { OpenAPIV3 } from 'openapi-types';

interface FilteredOpenAPIOptions {
    specUrl: string;
    includePaths: string[]; // Array of path patterns to include
}

async function fetchAndFilterOpenAPISpec({
    specUrl,
    includePaths
}: FilteredOpenAPIOptions): Promise<OpenAPIV3.Document> {
    try {
        // Fetch the OpenAPI spec
        const response = await axios.get(specUrl);
        const openApiSpec = response.data as OpenAPIV3.Document;

        // Create a filtered copy of the spec
        const filteredSpec: OpenAPIV3.Document = {
            openapi: openApiSpec.openapi,
            info: openApiSpec.info,
            servers: openApiSpec.servers,
            paths: {},
            components: openApiSpec.components, // Keep components as they might be referenced
            security: openApiSpec.security,
            tags: openApiSpec.tags,
            externalDocs: openApiSpec.externalDocs
        };

        // Filter paths based on includePaths patterns
        if (openApiSpec.paths) {
            Object.keys(openApiSpec.paths).forEach(path => {
                // Check if the path matches any of the include patterns
                const shouldInclude = includePaths.some(pattern => {
                    // Support wildcard patterns (e.g., "/users/*")
                    const regexPattern = pattern.replace(/\*/g, '.*');
                    const regex = new RegExp(`^${regexPattern}$`);
                    return regex.test(path);
                });

                if (shouldInclude && openApiSpec.paths) {
                    filteredSpec.paths[path] = openApiSpec.paths[path];
                }
            });
        }

        // Filter tags to only include those used in filtered paths
        if (filteredSpec.tags) {
            const usedTags = new Set<string>();

            Object.entries(filteredSpec.paths).forEach(([_, pathItem]) => {
                if (!pathItem) return; // Skip if pathItem is undefined

                // Get all HTTP methods from the path item
                const httpMethods = ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace'] as const;

                httpMethods.forEach(method => {
                    const operation = pathItem[method];
                    if (operation && typeof operation === 'object' && 'tags' in operation && operation.tags) {
                        operation.tags.forEach(tag => usedTags.add(tag));
                    }
                });
            });

            filteredSpec.tags = filteredSpec.tags.filter(tag => usedTags.has(tag.name));
        }

        return filteredSpec;
    } catch (error) {
        console.error('Error fetching or filtering OpenAPI spec:', error);
        throw error;
    }
}

export { fetchAndFilterOpenAPISpec };